/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { CORE_NAMESPACE, coreTranslations } from '@opengeoweb/core';
import {
  safelyInitI18nForTestsAndStories,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import timeseriesTranslations from '../../../locales/timeseries.json';

export const TIME_SERIES_NAMESPACE = 'timeseries';

export const initTimeseriesI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, TIME_SERIES_NAMESPACE, [
    { ns: TIME_SERIES_NAMESPACE, translations: timeseriesTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: CORE_NAMESPACE, translations: coreTranslations },
  ]);
};

const ns = [TIME_SERIES_NAMESPACE, SHARED_NAMESPACE, CORE_NAMESPACE];

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns, ...params });

export const useTimeseriesTranslation = (): UseTranslationResponse<
  typeof TIME_SERIES_NAMESPACE,
  typeof i18n
> => useTranslation(TIME_SERIES_NAMESPACE);
